import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import {
  Box,
  Button,
  InputAdornment,
  Grid,
  FormControl,
  MenuItem,
  Typography,
  Hidden
} from "@mui/material";
import { Formik, Form } from "formik";
import { useTranslation } from "react-i18next";
import { TextField, Select } from "formik-mui";
import { Field } from "formik";
import { states } from "../../../../constants";
import SearchIcon from "@mui/icons-material/Search";
import ButtonGroup from "../../../../components/Buttons/ButtonGroup";
import { useSelector } from "react-redux";
import moment from "moment";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";
import { DatePickerField } from "../../../../components/FormikFields";

const CourseExclusionsFilterDropdown = ({ fieldName, options }) => {
  const { t } = useTranslation();
  return (
    <Grid item xs={12} sm={6} md={4}>
      <FormControl fullWidth hiddenLabel>
        <Field
          name={fieldName}
          hiddenLabel
          component={Select}
          disableUnderline
          renderValue={val => {
            return (
              <Typography
                color={!!val ? "textPrimary" : "textSecondary"}
                variant="inherit"
              >{`${t(`courseExclusions.${fieldName}FilterLabel`)} ${
                !!val ? val : t("courseExclusions.filterValueShowAll")
              }`}</Typography>
            );
          }}
          variant="filled"
          fullWidth
          displayEmpty
          data-qa={`course-exclusions-filter-${fieldName}`}
          SelectDisplayProps={{
            "data-qa": "formik-field-select-btn"
          }}
        >
          <MenuItem value="">
            {`${t("courseExclusions.filterValueShowAll")}`}
          </MenuItem>
          {options.map(option => (
            <MenuItem key={option.value} value={option.value}>
              {option.value}
            </MenuItem>
          ))}
        </Field>
      </FormControl>
    </Grid>
  );
};

CourseExclusionsFilterDropdown.propTypes = {
  /**
   * Called when the form is submitted
   */
  options: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      value: PropTypes.string
    })
  )
};

const CourseExclusionsFiltersForm = ({
  resetForm,
  submitForm,
  setSubmitting,
  loading,
  isResetForm
}) => {
  const { t } = useTranslation();
  const [positionList, setPositionList] = useState([]);
  const [locationList, setLocationList] = useState([]);
  const [groupList, setGroupList] = useState([]);

  const positions = useSelector(state => state.contentCoursesState.positions);
  const locations = useSelector(state => state.contentCoursesState.locations);
  const groups = useSelector(state => state.contentCoursesState.groups);

  useEffect(() => {
    if (positions) {
      setPositionList(Object.values(positions));
    }
  }, [positions]);

  useEffect(() => {
    if (locations) {
      setLocationList(Object.values(locations));
    }
  }, [locations]);

  useEffect(() => {
    if (groups) {
      setGroupList(Object.values(groups));
    }
  }, [groups]);

  useEffect(() => {
    setSubmitting(loading);
  }, [loading]);

  useEffect(() => {
    if (isResetForm) {
      resetForm();
    }
  }, [isResetForm]);

  return (
    <Form>
      <LocalizationProvider dateAdapter={AdapterMoment}>
        <Grid container spacing={3}>
          <Grid item xs={12} sm={6} md={4}>
            <Field
              label={t("courseExclusions.employeeNameFilterLabel")}
              name={"employeeName"}
              variant="filled"
              fullWidth
              component={TextField}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <SearchIcon color="action" />
                  </InputAdornment>
                ),
                disableUnderline: true
              }}
            />
          </Grid>
          <CourseExclusionsFilterDropdown
            fieldName="position"
            options={positionList}
          />
          <CourseExclusionsFilterDropdown
            fieldName="location"
            options={locationList}
          />
          <CourseExclusionsFilterDropdown
            fieldName="group"
            options={groupList}
          />
          <CourseExclusionsFilterDropdown fieldName="state" options={states} />
          <Hidden mdDown>
            <Grid item xs={12} sm={6} md={4} />
          </Hidden>
          <Grid item xs={12} sm={6} md={4}>
            <Field
              name={"hiredBefore"}
              label={t("courseExclusions.hiredBeforeFilterLabel")}
              component={DatePickerField}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <Field
              name={"hiredSince"}
              label={t("courseExclusions.hiredSinceFilterLabel")}
              component={DatePickerField}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <Box
              display="flex"
              alignItems="center"
              height="100%"
              justifyContent="flex-end"
            >
              <ButtonGroup>
                <Button
                  color="primary"
                  onClick={() => {
                    resetForm();
                    submitForm();
                  }}
                >
                  {t("courseExclusions.clearAllBtn")}
                </Button>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={() => submitForm()}
                  data-qa="course-exclusions-filter-btn"
                >
                  {t("courseExclusions.filterListBtn")}
                </Button>
              </ButtonGroup>
            </Box>
          </Grid>
        </Grid>
      </LocalizationProvider>
    </Form>
  );
};
const CourseExclusionsFilters = ({ onFilterSubmit, loading, isResetForm }) => {
  const handleFilterSubmit = filters => {
    const filterList = { ...filters };
    if (filters.hiredSince) {
      var hiredSince = new Date(filters.hiredSince).setHours(0, 0, 0, 0);
      filterList.hiredSince = moment(hiredSince).format("YYYY-MM-DD");
    }
    if (filters.hiredBefore) {
      var hiredBefore = new Date(filters.hiredBefore).setHours(0, 0, 0, 0);
      filterList.hiredBefore = moment(hiredBefore).format("YYYY-MM-DD");
    }
    onFilterSubmit(filterList);
  };

  return (
    <Formik
      initialValues={{
        employeeName: "",
        state: "",
        position: "",
        location: "",
        group: "",
        hiredBefore: null,
        hiredSince: null
      }}
      onSubmit={(values, { setSubmitting }) => {
        handleFilterSubmit(values);
      }}
    >
      {({ submitForm, resetForm, setSubmitting }) => (
        <CourseExclusionsFiltersForm
          submitForm={submitForm}
          resetForm={resetForm}
          loading={loading}
          setSubmitting={setSubmitting}
          isResetForm={isResetForm}
        />
      )}
    </Formik>
  );
};
export default CourseExclusionsFilters;

CourseExclusionsFilters.propTypes = {
  /**
   * Called when the form is submitted
   */
  onFilterSubmit: PropTypes.func,
  /**
   * True if the exclusions data is loading
   */
  loading: PropTypes.bool,
  /**
   * True if the exclusions were updated and we have to clear all filters
   */
  isResetForm: PropTypes.bool
};
